"use client";

import { useState } from "react";
import { useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { t } from "@/lib/i18n/interpolate";
import { normalizeNumberInput, parseNumberInput } from "@/lib/parse-number";

const RE_WORD_SPLIT = /\s+/;

const ONES = [
  "",
  "one",
  "two",
  "three",
  "four",
  "five",
  "six",
  "seven",
  "eight",
  "nine",
  "ten",
  "eleven",
  "twelve",
  "thirteen",
  "fourteen",
  "fifteen",
  "sixteen",
  "seventeen",
  "eighteen",
  "nineteen",
];

const TENS = [
  "",
  "",
  "twenty",
  "thirty",
  "forty",
  "fifty",
  "sixty",
  "seventy",
  "eighty",
  "ninety",
];

const SCALES = ["", "thousand", "million", "billion", "trillion"];

function convertHundreds(n: number): string {
  if (n === 0) {
    return "";
  }
  if (n < 20) {
    return ONES[n];
  }
  if (n < 100) {
    const ten = TENS[Math.floor(n / 10)];
    const one = ONES[n % 10];
    return one ? `${ten}-${one}` : ten;
  }
  const hundred = `${ONES[Math.floor(n / 100)]} hundred`;
  const remainder = n % 100;
  if (remainder === 0) {
    return hundred;
  }
  return `${hundred} ${convertHundreds(remainder)}`;
}

/** The scale names stop at trillion, so the whole part can have at most 15 digits. */
const MAX_INTEGER_DIGITS = SCALES.length * 3;
const RE_SIGN = /^[+-]/;
const RE_LEADING_ZEROS = /^0+(?=\d)/;

/** True when the whole part of a number is too long for the largest scale name. */
export function exceedsNumberToWordsRange(input: string): boolean {
  const normalized = normalizeNumberInput(input);
  if (normalized === null) {
    return false;
  }
  const unsigned = normalized.replace(RE_SIGN, "");
  const integerPart = (unsigned.split(".")[0] || "0").replace(
    RE_LEADING_ZEROS,
    ""
  );
  return integerPart.length > MAX_INTEGER_DIGITS;
}

export function numberToWords(input: string): string {
  const normalized = normalizeNumberInput(input);
  if (normalized === null) {
    return "Please enter a valid number";
  }

  const isNegative = normalized.startsWith("-");
  const unsigned =
    isNegative || normalized.startsWith("+") ? normalized.slice(1) : normalized;
  const parts = unsigned.split(".");
  const integerPart = parts[0] || "0";
  const decimalPart = parts[1];

  if (integerPart === "0" && !decimalPart) {
    return "zero";
  }

  // Convert integer part
  const chunks: number[] = [];
  let remaining = integerPart;
  while (remaining.length > 0) {
    const chunk = remaining.slice(-3);
    chunks.push(Number.parseInt(chunk, 10));
    remaining = remaining.slice(0, -3);
  }

  const wordParts: string[] = [];
  for (let i = chunks.length - 1; i >= 0; i--) {
    if (chunks[i] === 0) {
      continue;
    }
    const words = convertHundreds(chunks[i]);
    if (SCALES[i]) {
      wordParts.push(`${words} ${SCALES[i]}`);
    } else {
      wordParts.push(words);
    }
  }

  let result = wordParts.join(" ") || "zero";

  if (decimalPart) {
    const decimalWords = decimalPart
      .split("")
      .map((d) => (d === "0" ? "zero" : ONES[Number.parseInt(d, 10)]))
      .join(" ");
    result = `${result} point ${decimalWords}`;
  }

  if (isNegative) {
    result = `negative ${result}`;
  }

  return result;
}

interface StyleStrings {
  apa7th: string;
  chicago: string;
  ap: string;
  spellOutApa: string;
  spellOutChicago: string;
  spellOutAp: string;
  numeralApa: string;
  numeralChicago: string;
}

function getStyleAdvice(
  input: string,
  strings: StyleStrings
): { style: string; advice: string }[] {
  const normalized = normalizeNumberInput(input);
  const value = parseNumberInput(input);
  if (normalized === null || value === null) {
    return [];
  }
  const num = Math.abs(value);
  const isWholeNumber = Number.isInteger(num);

  const advice: { style: string; advice: string }[] = [];

  // APA 7th
  if (isWholeNumber && num >= 0 && num <= 9) {
    advice.push({
      style: strings.apa7th,
      advice: t(strings.spellOutApa, { words: numberToWords(input) }),
    });
  } else {
    advice.push({
      style: strings.apa7th,
      advice: t(strings.numeralApa, { number: normalized }),
    });
  }

  // Chicago
  if (isWholeNumber && num >= 0 && num <= 100) {
    advice.push({
      style: strings.chicago,
      advice: t(strings.spellOutChicago, { words: numberToWords(input) }),
    });
  } else {
    advice.push({
      style: strings.chicago,
      advice: t(strings.numeralChicago, { number: normalized }),
    });
  }

  // AP
  if (isWholeNumber && num >= 1 && num <= 9) {
    advice.push({
      style: strings.ap,
      advice: t(strings.spellOutAp, { words: numberToWords(input) }),
    });
  } else {
    advice.push({
      style: strings.ap,
      advice: t(strings.numeralApa, { number: normalized }),
    });
  }

  return advice;
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const NUMBER_TO_WORDS_STRINGS = {
  enterANumber: "Enter a Number",
  inputPlaceholder: "Enter a number (e.g. 42, -3.14, 1000000)",
  writtenForm: "Written Form",
  copied: "Copied!",
  copyToClipboard: "Copy to Clipboard",
  wordsInResult: "Words in Result",
  charactersInResult: "Characters in Result",
  styleGuideRules: "Style Guide Rules",
  numberTooLargeMessage:
    "That number has too many digits. This tool writes whole parts of up to 15 digits (999 trillion); enter a smaller number.",
  invalidNumberMessage:
    "Please enter a valid number. Supported formats: integers (42), decimals (3.14), negative numbers (-7), and large numbers (1000000).",
  apa7th: "APA 7th",
  chicago: "Chicago",
  ap: "AP",
  spellOutApa:
    'Spell out: "{words}" (numbers zero through nine are written as words)',
  spellOutChicago:
    'Spell out: "{words}" (numbers zero through one hundred are written as words)',
  spellOutAp:
    'Spell out: "{words}" (numbers one through nine are written as words)',
  numeralApa:
    "Use the numeral: {number} (numbers 10 and above are written as numerals)",
  numeralChicago:
    "Use the numeral: {number} (numbers above one hundred are written as numerals)",
} as const;

export default function NumberToWordsWidget() {
  const [input, setInput] = useState("");
  const [copied, setCopied] = useState(false);

  const strings = useToolUi(NUMBER_TO_WORDS_STRINGS);

  const tooLarge = exceedsNumberToWordsRange(input);
  const isValid = normalizeNumberInput(input) !== null && !tooLarge;
  const words = isValid ? numberToWords(input) : "";
  const styleAdvice = isValid ? getStyleAdvice(input, strings) : [];
  const wordCount = words
    ? words.split(RE_WORD_SPLIT).filter(Boolean).length
    : 0;
  const charCount = words.length;

  async function handleCopy() {
    if (words) {
      if (!(await writeToClipboard(words))) {
        return;
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }

  return (
    <>
      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="num-input"
        >
          {strings.enterANumber}
        </label>
        <input
          className="w-full rounded-lg border border-border bg-card p-4 text-foreground text-lg leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="num-input"
          inputMode="decimal"
          onChange={(e) => setInput(e.target.value)}
          placeholder={strings.inputPlaceholder}
          type="text"
          value={input}
        />
      </div>

      {/* Result */}
      {isValid && (
        <>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="font-medium text-foreground text-sm">
                {strings.writtenForm}
              </p>
              <button
                className="rounded-md border border-border px-3 py-1 text-muted-foreground text-xs transition-colors hover:bg-accent hover:text-foreground"
                onClick={handleCopy}
                type="button"
              >
                {copied ? strings.copied : strings.copyToClipboard}
              </button>
            </div>
            <div className="rounded-lg border border-border bg-muted/30 p-4">
              <p className="text-foreground text-lg leading-relaxed">{words}</p>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-4">
            <StatCard label={strings.wordsInResult} value={wordCount} />
            <StatCard label={strings.charactersInResult} value={charCount} />
          </div>

          {/* Style Guide Advice */}
          <section className="space-y-3">
            <h2 className="font-semibold text-foreground text-lg">
              {strings.styleGuideRules}
            </h2>
            <div className="space-y-3">
              {styleAdvice.map((item) => (
                <div
                  className="rounded-lg border border-border bg-card p-4"
                  key={item.style}
                >
                  <p className="font-medium text-foreground text-sm">
                    {item.style}
                  </p>
                  <p className="mt-1 text-muted-foreground text-sm">
                    {item.advice}
                  </p>
                </div>
              ))}
            </div>
          </section>
        </>
      )}

      {input.length > 0 && !isValid && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700 text-sm dark:border-red-800 dark:bg-red-950 dark:text-red-300">
          {tooLarge
            ? strings.numberTooLargeMessage
            : strings.invalidNumberMessage}
        </div>
      )}
    </>
  );
}
